import { Timestamp } from 'firebase/firestore';

/**
 * Converts a Firestore Timestamp or Date to a JavaScript Date object
 */
export function toDate(timestamp: Timestamp | Date | unknown): Date {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    return (timestamp as Timestamp).toDate();
  }
  if (timestamp) {
    return new Date(timestamp as string);
  }
  return new Date();
}

/**
 * Formats a date for display in the UI
 * Returns 'N/A' if the date is invalid
 */
export function formatDate(date: Date | Timestamp | unknown): string {
  try {
    const d = toDate(date);
    if (isNaN(d.getTime())) {
      return 'N/A';
    }
    return d.toLocaleDateString();
  } catch {
    return 'N/A';
  }
}

/**
 * Formats a date with time for display in the UI
 */
export function formatDateTime(date: Date | Timestamp | unknown): string {
  try {
    const d = toDate(date);
    if (isNaN(d.getTime())) {
      return 'N/A';
    }
    return d.toLocaleString();
  } catch {
    return 'N/A';
  }
}

/**
 * Returns a relative time string (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(date: Date | Timestamp | unknown): string {
  try {
    const d = toDate(date);
    if (isNaN(d.getTime())) {
      return 'N/A';
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    }
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    
    return formatDate(d);
  } catch {
    return 'N/A';
  }
}




